/**
 * Ezi Shizi content schema.
 *
 * Two layers live under content/:
 *  - the FACTUAL layer (editions, levels, lessons, characters): what MOE publishes,
 *    imported mechanically and never hand-edited except to fix import errors;
 *  - the AUTHORED layer (words, sentences, instructions, lesson titles, set order):
 *    written by us. Nothing from third-party apps is allowed here.
 *
 * Build output (LessonBundle) is derived from both and is what the app loads.
 */
import { z } from 'zod';
import {
  EditionId,
  Hanzi,
  HanziString,
  Id,
  LevelId,
  LocalisedText,
  MediaId,
  Pinyin,
  Semester,
  SentenceToken,
  Skill,
} from './primitives.ts';
import { Passage, Practice, PracticeQuestion } from './practice.ts';

// ---------- primitives ----------
// Moved to primitives.ts (re-exported below) so practice.ts can depend on them without an import
// cycle back into this file, which itself needs practice.ts's types for LessonBundle.
export {
  EditionId,
  Hanzi,
  HanziString,
  Id,
  LevelId,
  LocalisedText,
  MediaId,
  Pinyin,
  Semester,
  Skill,
};

// ---------- factual layer ----------

export const CharacterList = z.object({
  id: Id,
  title: z.string(),
  url: z.string().url(),
  covers: z.array(LevelId),
  higher: z.boolean(),
});

export const Edition = z.object({
  id: EditionId,
  year: z.number().int(),
  label: z.string(),
  syllabus: z.object({ title: z.string(), url: z.string().url() }),
  characterLists: z.array(CharacterList).min(1),
  levels: z.array(LevelId).min(1),
});
export type Edition = z.infer<typeof Edition>;

export const Level = z.object({
  id: LevelId,
  editionId: EditionId,
  order: z.number().int().positive(),
  title: LocalisedText,
  lessonIds: z.array(Id).min(1),
});
export type Level = z.infer<typeof Level>;

/** One MOE character within one lesson. */
export const CharacterEntry = z.object({
  char: Hanzi,
  /** stable id, e.g. sg-zh-moe-p1a-l01-01 (kept from MOE list order) */
  id: Id,
  order: z.number().int().positive(),
  skills: z.array(Skill).min(1),
  /** Character only appears in the Higher Chinese list. */
  higherOnly: z.boolean(),
  /** Write target only in Higher Chinese (recognise-only in standard). */
  higherWrite: z.boolean(),
  sourceId: Id,
  /** Authored: the teaching word for this character in this lesson. null until authored. */
  wordId: Id.nullable(),
});
export type CharacterEntry = z.infer<typeof CharacterEntry>;

/** A group of ~5 characters = one practice session. Order is authored. */
export const LessonSet = z.object({
  id: Id,
  order: z.number().int().positive(),
  entries: z.array(CharacterEntry).min(1),
});
export type LessonSet = z.infer<typeof LessonSet>;

export const Lesson = z.object({
  id: Id,
  levelId: LevelId,
  editionId: EditionId,
  order: z.number().int().positive(),
  semester: Semester,
  /** Authored theme title. null until authored (MOE lists only number lessons). */
  title: LocalisedText.nullable(),
  sets: z.array(LessonSet).min(1),
});
export type Lesson = z.infer<typeof Lesson>;

// ---------- components (authored, content/components.json) ----------
// A teaching decomposition for the character-builder game: the pieces a child already knows that
// make up a character (妈 = 女 + 马). Teaching splits, not strict etymology: every leaf is either a
// character taught at or before the character's own lesson, or one of the radical shapes below.

/** Ideographic description operators and how many operands each takes. */
export const IDS_OPERATORS: Readonly<Record<string, 2 | 3>> = {
  '⿰': 2,
  '⿱': 2,
  '⿲': 3,
  '⿳': 3,
  '⿴': 2,
  '⿵': 2,
  '⿶': 2,
  '⿷': 2,
  '⿸': 2,
  '⿹': 2,
  '⿺': 2,
  '⿻': 2,
};

/** Radical shapes allowed as builder pieces although they are never taught as characters. */
export const COMPONENT_SHAPES: readonly string[] = [
  '亻',
  '氵',
  '扌',
  '讠',
  '艹',
  '犭',
  '饣',
  '衤',
  '纟',
  '忄',
  '阝',
  '宀',
  '囗',
  '冖',
  '爫',
  '勹',
  '辶',
  '礻',
];

/** The leaves of an IDS expression in reading order ("⿰口⿱日日" → 口 日 日). */
export const idsLeaves = (ids: string): string[] => [...ids].filter((c) => !(c in IDS_OPERATORS));

/**
 * Whether an IDS expression is well formed: every operator has exactly its operands and nothing
 * is left over. Leaves are any single code point that is not an operator.
 */
export function idsWellFormed(ids: string): boolean {
  const cps = [...ids];
  let i = 0;
  const node = (): boolean => {
    const c = cps[i++];
    if (c === undefined) return false;
    const arity = IDS_OPERATORS[c];
    if (!arity) return true;
    for (let k = 0; k < arity; k++) if (!node()) return false;
    return true;
  };
  return node() && i === cps.length;
}

export const Components = z
  .object({
    /** An IDS expression whose leaves are the pieces, e.g. "⿰女马" or "⿰口⿱日日". */
    ids: z.string().min(2).refine(idsWellFormed, 'malformed IDS expression'),
    /** The leaves of `ids`, in order: what the child drags. One to three. */
    parts: z.array(z.string().min(1)).min(1).max(3),
  })
  .refine((c) => idsLeaves(c.ids).join('') === c.parts.join(''), {
    message: 'parts must be the leaves of ids, in order',
  });
export type Components = z.infer<typeof Components>;

/** Global, level-independent character facts (pypinyin + Make Me a Hanzi). */
export const Character = z.object({
  char: Hanzi,
  /** All dictionary readings, most common first. */
  pinyin: z.array(Pinyin).min(1),
  radical: z.string().min(1),
  strokeCount: z.number().int().positive(),
  /** Authored teaching decomposition (content/components.json), merged in by the build. */
  components: Components.optional(),
});
export type Character = z.infer<typeof Character>;

// ---------- authored layer ----------

export const WordKind = z.enum(['picture', 'scene', 'abstract']);
export type WordKind = z.infer<typeof WordKind>;

/** Sorting categories for the two-baskets game. `word` marks particles, adjectives and other
 * words that are not sortable; it is the explicit "not this game" tag. */
export const WordCategory = z.enum([
  'animal',
  'food',
  'drink',
  'body',
  'family',
  'person',
  'clothing',
  'nature',
  'weather',
  'place',
  'school',
  'thing',
  'number',
  'measure',
  'action',
  'feeling',
  'time',
  'colour',
  'word',
]);
export type WordCategory = z.infer<typeof WordCategory>;

/** Numerals for the count game's phrases, index = count. Two objects say 两, not 二. */
export const COUNT_NUMERALS = [
  '',
  '一',
  '两',
  '三',
  '四',
  '五',
  '六',
  '七',
  '八',
  '九',
  '十',
] as const;

/** The spoken count phrase for a tagged noun: countPhrase(3, '条', '鱼') → 三条鱼. */
export const countPhrase = (n: number, measure: string, text: string): string =>
  `${COUNT_NUMERALS[n] ?? String(n)}${measure}${text}`;

/** The English line Shizi says when a lesson session starts, e.g. "Lesson 1: Picture characters." */
export function lessonTitleLine(order: number, titleEn: string): string {
  return `Lesson ${order}: ${titleEn.replace(/[.!?]+$/, '')}.`;
}

/** How many count phrases the build emits audio for per countable noun (1..COUNT_MAX). */
export const COUNT_MAX = 5;

export const Word = z.object({
  id: Id,
  text: HanziString,
  /** Space-separated syllables with tone marks, one per character: "yī fu". */
  pinyin: Pinyin,
  meaningEn: z.string().min(1),
  kind: WordKind,
  /** Sorting category for the two-baskets game; absent means the same as `word`. */
  category: WordCategory.optional(),
  /** The measure word this noun takes (鱼 → 条), for the count game. One hanzi. */
  measure: Hanzi.optional(),
  /** Author hint describing the subject for illustration generation, e.g. "a child's ear, side view". */
  imagePrompt: z.string().nullable().optional(),
  /** Prototype-only placeholder shown when no illustration exists yet. */
  emoji: z.string().nullable(),
  /** Media ids resolve to R2 keys at build time. */
  imageId: MediaId.nullable(),
  audioId: MediaId.nullable(),
  sentenceIds: z.array(Id),
});
export type Word = z.infer<typeof Word>;

export { SentenceToken };

export const SentenceMode = z.enum(['listen', 'read']);
export type SentenceMode = z.infer<typeof SentenceMode>;

export const Sentence = z.object({
  id: Id,
  text: z.string().min(1),
  /**
   * listen: the child hears the sentence and sees a picture; untaught hanzi may appear.
   * read: the child must read it (sentence completion / building); every hanzi must be taught.
   */
  mode: SentenceMode,
  tokens: z.array(SentenceToken).min(1),
  meaningEn: z.string().min(1),
  audioId: MediaId.nullable(),
  /** Every hanzi must be taught by this lesson (or be a sight word). */
  allowedFromLesson: Id,
  /** Token indices that may be blanked for sentence-completion exercises. */
  blankableTokenIdx: z.array(z.number().int().nonnegative()),
});
export type Sentence = z.infer<typeof Sentence>;

export const Instruction = z.object({
  id: Id,
  text: LocalisedText,
  audio: z.object({ zh: MediaId.nullable(), en: MediaId.nullable() }),
  icon: z.string().min(1),
});
export type Instruction = z.infer<typeof Instruction>;

// ---------- stories ----------
// A story is data, not bespoke art: every scene is composed on a stage from the mascot and the
// lesson's word illustrations, with one sentence and one interaction.

export const MascotPose = z.enum(['idle', 'cheer', 'think', 'still']);

export const StageActor = z.object({
  id: Id,
  kind: z.enum(['mascot', 'word']),
  wordId: Id.nullable(),
  pose: MascotPose.nullable(),
  /** position of the actor's centre, percent of the stage */
  x: z.number().min(0).max(100),
  y: z.number().min(0).max(100),
  scale: z.number().positive(),
  rotate: z.number(),
  /** drawn behind the others (e.g. rain) */
  behind: z.boolean(),
});
export type StageActor = z.infer<typeof StageActor>;

export const SceneInteraction = z.discriminatedUnion('type', [
  z.object({ type: z.literal('listen') }),
  z.object({ type: z.literal('tap'), targetActorId: Id }),
  z.object({ type: z.literal('say'), wordId: Id }),
]);
export type SceneInteraction = z.infer<typeof SceneInteraction>;

export const StoryScene = z.object({
  id: Id,
  text: z.string().min(1),
  pinyin: Pinyin,
  meaningEn: z.string().min(1),
  audioId: MediaId.nullable(),
  background: z.enum(['none', 'room', 'outside', 'rain', 'water']),
  actors: z.array(StageActor).min(1),
  interaction: SceneInteraction,
});
export type StoryScene = z.infer<typeof StoryScene>;

export const Story = z.object({
  id: Id,
  lessonId: Id,
  title: LocalisedText,
  scenes: z.array(StoryScene).min(1),
});
export type Story = z.infer<typeof Story>;

// ---------- scene games (content/scenes/<lessonId>.json) ----------
// Which scene: two or three variants of a stage that differ in an action, a relation, a
// quantity or a negation; the child hears a lesson sentence and taps the scene it describes.
// Listen and build: a stage with named slots; the child hears an instruction and moves the
// object it names to the place it names. Its tasks are composed, not authored: see builds.ts.

export const SceneBackground = z.enum(['none', 'room', 'outside', 'rain', 'water']);
export type SceneBackground = z.infer<typeof SceneBackground>;

export const SceneVariant = z.object({
  id: Id,
  background: SceneBackground,
  actors: z.array(StageActor).min(1),
});
export type SceneVariant = z.infer<typeof SceneVariant>;

export const SceneSet = z.object({
  id: Id,
  lessonId: Id,
  /** The read-mode sentence the child hears. */
  sentenceId: Id,
  /** The variant that matches it. */
  answerId: Id,
  scenes: z.array(SceneVariant).min(2).max(3),
});
export type SceneSet = z.infer<typeof SceneSet>;

export const BuildSlot = z.object({
  id: Id,
  /** The word whose picture labels the slot (a person, a place, a container). */
  wordId: Id,
  x: z.number().min(0).max(100),
  y: z.number().min(0).max(100),
});
export type BuildSlot = z.infer<typeof BuildSlot>;

export const BuildTemplate = z.enum(['place', 'have']);
export type BuildTemplate = z.infer<typeof BuildTemplate>;

/** One Listen-and-build task, composed per set at build time (see `composeBuildTasks`). */
export const BuildTask = z.object({
  id: Id,
  lessonId: Id,
  setId: Id,
  /** place: X在Y里 / X在Y上 (from lesson 6); have: Y有X (from lesson 10). */
  template: BuildTemplate,
  /** The instruction, spoken; Chinese only. */
  text: z.string().min(1),
  pinyin: Pinyin,
  meaningEn: z.string().min(1),
  audioId: MediaId.nullable(),
  background: SceneBackground,
  /** Words offered as movable objects: the named one(s) plus a distractor. */
  objectIds: z.array(Id).min(1).max(3),
  slots: z.array(BuildSlot).min(2).max(3),
  /** What has to end up where: one placement, or two for a two-part instruction
   * (鸟在河里，刀在桌子上). */
  targets: z
    .array(z.object({ objectId: Id, slotId: Id }))
    .min(1)
    .max(2),
});
export type BuildTask = z.infer<typeof BuildTask>;

/** One lesson's scene content: `content/scenes/<lessonId>.json`. */
export const LessonScenes = z.object({
  lessonId: Id,
  sceneSets: z.array(SceneSet).default([]),
});
export type LessonScenes = z.infer<typeof LessonScenes>;

/** Hanzi allowed in sentences before they are formally taught. */
export const SightWords = z.object({ chars: z.array(Hanzi) });

// ---------- build output ----------

export const MediaRef = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('url'),
    url: z.string().min(1),
    bytes: z.number().int().nonnegative().optional(),
  }),
  /** Prototype placeholder for an illustration. */
  z.object({ kind: z.literal('emoji'), value: z.string().min(1) }),
  /** No clip available: the app falls back to browser speech synthesis for this text. */
  z.object({ kind: z.literal('tts'), text: z.string().min(1), lang: z.enum(['zh-CN', 'en-SG']) }),
]);
export type MediaRef = z.infer<typeof MediaRef>;

export const LessonBundle = z.object({
  schemaVersion: z.literal(1),
  contentHash: z.string(),
  lesson: Lesson,
  characters: z.record(Hanzi, Character),
  /** Every word in the level, as a distractor pool: later lessons' words included. */
  words: z.record(Id, Word),
  /** The words taught at or before this lesson, in teaching order: what a round may *ask*
   * about. `words` also carries later lessons' words, which a child has not met yet. */
  knownWordIds: z.array(Id).default([]),
  sentences: z.record(Id, Sentence),
  stories: z.record(Id, Story),
  /** Which-scene sets and Listen-and-build tasks for this lesson (may be empty). */
  sceneSets: z.record(Id, SceneSet).default({}),
  builds: z.record(Id, BuildTask).default({}),
  /** P3+ reading practice: a lesson's passage(s), their questions, and the practice set(s) that
   * tie them together (`practice.ts`). Empty for a P1–P2 lesson. */
  passages: z.record(Id, Passage).default({}),
  practiceQuestions: z.record(Id, PracticeQuestion).default({}),
  practices: z.record(Id, Practice).default({}),
  instructions: z.record(Id, Instruction),
  /** Lesson-independent spoken phrases (praise, prompts); audio at `audio:phrase:<id>:zh|en`. */
  phrases: z.record(Id, LocalisedText).default({}),
  media: z.record(MediaId, MediaRef),
});
export type LessonBundle = z.infer<typeof LessonBundle>;

export const ContentIndex = z.object({
  schemaVersion: z.literal(1),
  builtAt: z.string(),
  editions: z.array(Edition),
  levels: z.array(Level),
  lessons: z.array(
    z.object({
      id: Id,
      levelId: LevelId,
      order: z.number().int(),
      title: LocalisedText.nullable(),
      /** true once every entry has an authored word; only authored lessons get a bundle */
      authored: z.boolean(),
      characterCount: z.number().int(),
      setCount: z.number().int(),
      /** Per-set summary so the path/lesson screens can be drawn without loading the bundle. */
      sets: z
        .array(
          z.object({
            id: Id,
            order: z.number().int().positive(),
            /** Standard-list characters in authored order (Higher Chinese extras omitted). */
            chars: z.string(),
            /** Whether the set has anything to write (standard list / with Higher Chinese). */
            hasWrite: z.boolean().default(false),
            hasHigherWrite: z.boolean().default(false),
            /** The characters of `chars` that are write targets (standard list / with Higher
             * Chinese), so the word-first islands know which words get a Write game. */
            writeChars: z.string().default(''),
            higherWriteChars: z.string().default(''),
            /**
             * The minigame formats this set can actually build a round of, computed at build
             * time by running every format's builder against it. A format is not available
             * everywhere — Feed Shizi needs food in the set, Which scene needs authored
             * contrastive scenes — and the island's stops are chosen from this list, so the
             * path can be drawn without loading a bundle.
             */
            games: z.array(z.string()).default([]),
            /**
             * How many questions each format in `games` builds on this set. A format that builds
             * one or two is a thin stop on its own (the island tops it up from other formats), so
             * the island picker prefers a full format where the slot has one.
             */
            gameSizes: z.record(z.string(), z.number().int()).default({}),
            /**
             * The set's word pictures, so the adventure islands can be drawn from the index
             * alone (a lesson bundle is several hundred KB). `picture` is true for words whose
             * illustration depicts the thing itself (props), false for scenes and concepts.
             */
            pictures: z
              .array(z.object({ char: Hanzi, media: MediaRef, picture: z.boolean() }))
              .default([]),
          }),
        )
        .default([]),
      storyIds: z.array(Id).default([]),
      /** The lesson's reading practice set (P3+), when one has been authored; null everywhere
       * else so the path screen can tell without loading the bundle. */
      practiceId: Id.nullable().default(null),
      url: z.string().nullable(),
    }),
  ),
});
export type ContentIndex = z.infer<typeof ContentIndex>;

export const DEFAULT_SIGHT_WORDS = [
  '的',
  '是',
  '我',
  '你',
  '他',
  '她',
  '了',
  '吗',
  '在',
  '和',
  '有',
  '不',
  '这',
  '那',
];

export * from './builds.ts';
export * from './practice.ts';
